import React, { useState } from 'react';

const DataManager = ({ sessions, tasks, onDataImport }) => {
  const [importData, setImportData] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [importStatus, setImportStatus] = useState('');

  const exportToJSON = () => {
    const data = {
      sessions,
      tasks,
      achievements: JSON.parse(localStorage.getItem('achievements') || '[]'),
      pomodoroSettings: JSON.parse(localStorage.getItem('pomodoroSettings') || '{}'),
      focusModeSettings: JSON.parse(localStorage.getItem('focusModeSettings') || '{}'),
      exportDate: new Date().toISOString(),
      version: '1.0'
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `study-timer-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportToCSV = () => {
    // Export sessions to CSV
    const csvHeaders = [
      'Date',
      'Subject',
      'Duration (minutes)',
      'Type',
      'Task ID',
      'Completed At'
    ].join(',');

    const csvRows = sessions.map(session => [
      session.date,
      `"${session.subject}"`,
      Math.round(session.duration / 60),
      session.type || 'regular',
      session.taskId || '',
      session.completedAt
    ].join(','));

    const csvContent = [csvHeaders, ...csvRows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `study-sessions-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportStatistics = () => {
    const stats = generateStatistics();
    const statsText = Object.entries(stats)
      .map(([key, value]) => `${key}: ${value}`)
      .join('\n');

    const blob = new Blob([statsText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `study-statistics-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const generateStatistics = () => {
    const totalTime = sessions.reduce((sum, s) => sum + s.duration, 0);
    const totalSessions = sessions.length;
    const completedTasks = tasks.filter(t => t.completed).length;
    const totalTasks = tasks.length;
    const pomodoroSessions = sessions.filter(s => s.type === 'pomodoro').length;

    // Daily averages
    const uniqueDays = new Set(sessions.map(s => s.date)).size;
    const avgSessionsPerDay = uniqueDays > 0 ? (totalSessions / uniqueDays).toFixed(2) : 0;
    const avgTimePerDay = uniqueDays > 0 ? ((totalTime / 60) / uniqueDays).toFixed(2) : 0;

    // Subject breakdown
    const subjectStats = {};
    sessions.forEach(s => {
      subjectStats[s.subject] = (subjectStats[s.subject] || 0) + s.duration;
    });

    const topSubjects = Object.entries(subjectStats)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([subject, time]) => `${subject}: ${Math.round(time / 60)} minutes`)
      .join(', ');

    // Time of day analysis
    const hourlyStats = Array(24).fill(0);
    sessions.forEach(s => {
      const hour = new Date(s.completedAt).getHours();
      hourlyStats[hour] += s.duration;
    });
    const peakHour = hourlyStats.indexOf(Math.max(...hourlyStats));

    return {
      'Export Date': new Date().toLocaleString(),
      'Total Study Time (hours)': (totalTime / 3600).toFixed(2),
      'Total Sessions': totalSessions,
      'Average Session Length (minutes)': totalSessions > 0 ? (totalTime / 60 / totalSessions).toFixed(2) : 0,
      'Total Tasks': totalTasks,
      'Completed Tasks': completedTasks,
      'Task Completion Rate': totalTasks > 0 ? `${((completedTasks / totalTasks) * 100).toFixed(1)}%` : '0%',
      'Pomodoro Sessions': pomodoroSessions,
      'Study Days': uniqueDays,
      'Average Sessions per Day': avgSessionsPerDay,
      'Average Study Time per Day (minutes)': avgTimePerDay,
      'Peak Study Hour': `${peakHour}:00`,
      'Top 5 Subjects': topSubjects || 'None',
      'Unique Subjects Studied': Object.keys(subjectStats).length
    };
  };

  const importFromJSON = () => {
    try {
      const data = JSON.parse(importData);

      // Validate data structure
      if (!data.sessions || !Array.isArray(data.sessions)) {
        throw new Error('Invalid data format: sessions array not found');
      }
      if (!data.tasks || !Array.isArray(data.tasks)) {
        throw new Error('Invalid data format: tasks array not found');
      }

      // Import data
      onDataImport(data);

      // Import other settings
      if (data.achievements) {
        localStorage.setItem('achievements', JSON.stringify(data.achievements));
      }
      if (data.pomodoroSettings) {
        localStorage.setItem('pomodoroSettings', JSON.stringify(data.pomodoroSettings));
      }
      if (data.focusModeSettings) {
        localStorage.setItem('focusModeSettings', JSON.stringify(data.focusModeSettings));
      }

      setImportStatus('✅ Data imported successfully! Please refresh the page to see changes.');
      setImportData('');
      setShowImport(false);
    } catch (error) {
      setImportStatus(`❌ Import failed: ${error.message}`);
    }
  };

  const clearAllData = () => {
    if (window.confirm('⚠️ Are you sure you want to clear ALL data? This action cannot be undone!')) {
      if (window.confirm('🔴 FINAL WARNING: This will permanently delete all your study sessions, tasks, and achievements. Continue?')) {
        localStorage.clear();
        onDataImport({ sessions: [], tasks: [] });
        setImportStatus('🗑️ All data has been cleared. Please refresh the page.');
      }
    }
  };

  const getDataSize = () => {
    const dataSize = new Blob([JSON.stringify({ sessions, tasks })]).size;
    return (dataSize / 1024).toFixed(2) + ' KB';
  };

  const createBackupReminder = () => {
    const lastBackup = localStorage.getItem('lastBackupDate');
    const today = new Date().toDateString();

    if (!lastBackup || lastBackup !== today) {
      return (
        <div className="backup-reminder">
          <span className="reminder-icon">💾</span>
          <span>Reminder: Consider backing up your data regularly!</span>
          <button
            onClick={() => {
              exportToJSON();
              localStorage.setItem('lastBackupDate', today);
            }}
            className="quick-backup-btn"
          >
            Quick Backup
          </button>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="data-manager">
      <div className="data-header">
        <h2>💾 Data Management</h2>
        <div className="data-info">
          <span>Current data size: {getDataSize()}</span>
          <span>Sessions: {sessions.length}</span>
          <span>Tasks: {tasks.length}</span>
        </div>
      </div>

      {createBackupReminder()}

      <div className="export-section">
        <h3>📤 Export Data</h3>
        <div className="export-buttons">
          <button onClick={exportToJSON} className="export-btn json">
            <span className="btn-icon">📄</span>
            <div className="btn-content">
              <div className="btn-title">Export as JSON</div>
              <div className="btn-description">Complete backup with all settings</div>
            </div>
          </button>

          <button onClick={exportToCSV} className="export-btn csv">
            <span className="btn-icon">📊</span>
            <div className="btn-content">
              <div className="btn-title">Export as CSV</div>
              <div className="btn-description">Study sessions for analysis</div>
            </div>
          </button>

          <button onClick={exportStatistics} className="export-btn stats">
            <span className="btn-icon">📈</span>
            <div className="btn-content">
              <div className="btn-title">Export Statistics</div>
              <div className="btn-description">Summary report as text file</div>
            </div>
          </button>
        </div>
      </div>

      <div className="import-section">
        <h3>📥 Import Data</h3>
        <button
          onClick={() => setShowImport(!showImport)}
          className="import-toggle-btn"
        >
          {showImport ? 'Cancel Import' : 'Import from JSON'}
        </button>

        {showImport && (
          <div className="import-form">
            <textarea
              value={importData}
              onChange={(e) => setImportData(e.target.value)}
              placeholder="Paste your JSON backup data here..."
              className="import-textarea"
              rows="10"
            />
            <div className="import-actions">
              <button onClick={importFromJSON} className="import-btn">
                Import Data
              </button>
              <button
                onClick={() => {
                  setShowImport(false);
                  setImportData('');
                  setImportStatus('');
                }}
                className="cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {importStatus && (
          <div className={`import-status ${importStatus.includes('failed') ? 'error' : 'success'}`}>
            {importStatus}
          </div>
        )}
      </div>

      <div className="statistics-preview">
        <h3>📊 Quick Statistics</h3>
        <div className="stats-grid">
          {Object.entries(generateStatistics()).slice(0, 8).map(([key, value]) => (
            <div key={key} className="stat-item">
              <div className="stat-label">{key}</div>
              <div className="stat-value">{value}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="sync-section">
        <h3>☁️ Sync Options</h3>
        <div className="sync-info">
          <p>💡 <strong>Pro Tip:</strong> Your data is stored locally in your browser. To sync across devices:</p>
          <ul>
            <li>Export your data on one device</li>
            <li>Import it on another device</li>
            <li>Set up regular backups to avoid data loss</li>
          </ul>
        </div>
      </div>

      <div className="danger-zone">
        <h3>⚠️ Danger Zone</h3>
        <button onClick={clearAllData} className="danger-btn">
          🗑️ Clear All Data
        </button>
        <p className="danger-warning">
          This will permanently delete all your study sessions, tasks, achievements, and settings.
        </p>
      </div>

      <div className="data-tips">
        <h4>💡 Data Management Tips</h4>
        <ul>
          <li>Export your data regularly to avoid losing progress</li>
          <li>Use JSON export for complete backups with all settings</li>
          <li>Use CSV export for analyzing your study patterns in spreadsheet apps</li>
          <li>Keep multiple backup files from different dates</li>
          <li>Test imports with a small dataset first</li>
        </ul>
      </div>
    </div>
  );
};

export default DataManager;